import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { FiLink, FiTrash2 } from "react-icons/fi";
import { AppButton, MessageBanner } from "@/components/ui/primitives";
import { confirmAction } from "@/components/ui/confirmDialog";
import { useAuthStore } from "@/stores/useAuthStore";
import { useTranslation } from "react-i18next";
import { mcpGrantsQueryKey, revokeMcpGrant, useMcpGrants, type McpGrant } from "@/domains/mcp/hooks/useMcpOAuth";
import { formatMcpDate, isMcpDateExpired } from "@/domains/mcp/utils/date";

export const McpGrantsPanel = () => {
  const { t, i18n } = useTranslation(["profile", "common"]);
  const queryClient = useQueryClient();
  const hasSession = useAuthStore((state) => Boolean(state.token));
  const userId = useAuthStore((state) => state.user?.id);
  const grantsQuery = useMcpGrants(userId, hasSession);
  const [isRevokingId, setIsRevokingId] = useState<number | null>(null);
  const [error, setError] = useState(false);

  const handleRevoke = async (grant: McpGrant) => {
    const confirmed = await confirmAction({
      title: t("profile.mcpConsent.revokeTitle", { client: grant.client_name }),
      body: t("profile.mcpConsent.revokeBody"),
      confirmLabel: t("profile.mcpConsent.revoke"),
      cancelLabel: t("common.actions.cancel"),
      tone: "danger",
    });
    if (!confirmed) return;

    setIsRevokingId(grant.id);
    setError(false);
    try {
      await revokeMcpGrant(grant.id);
      await queryClient.invalidateQueries({ queryKey: mcpGrantsQueryKey(userId) });
    } catch {
      setError(true);
    } finally {
      setIsRevokingId(null);
    }
  };

  return (
    <details className="app-panel-raised group mt-6 p-6 sm:p-7" open>
      <summary className="flex cursor-pointer list-none items-start justify-between gap-4 [&::-webkit-details-marker]:hidden">
        <div>
          <p className="app-domain-sidebar-label">{t("profile.mcp.eyebrow")}</p>
          <h2 className="text-lg font-semibold tracking-[-0.03em] text-[var(--app-text)] sm:text-xl">
            {t("profile.mcpConsent.connectionsTitle")}
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-[var(--app-text-secondary)]">
            {t("profile.mcpConsent.connectionsDescription")}
          </p>
        </div>
        <FiLink aria-hidden="true" className="mt-1 h-5 w-5 shrink-0 text-[var(--app-text-secondary)]" />
      </summary>

      <div className="mt-5" aria-live="polite" aria-busy={grantsQuery.isLoading}>
        {error ? <MessageBanner role="alert" tone="error">{t("profile.mcpConsent.revokeError")}</MessageBanner> : null}
        {grantsQuery.isLoading ? (
          <p className="text-sm text-[var(--app-text-secondary)]">{t("profile.mcpConsent.loading")}</p>
        ) : grantsQuery.isError ? (
          <div>
            <p role="alert" className="text-sm text-[var(--app-danger)]">{t("profile.mcpConsent.loadError")}</p>
            <AppButton type="button" tone="ghost" size="sm" className="mt-2" onClick={() => void grantsQuery.refetch()}>
              {t("common.actions.retry", { defaultValue: "Retry" })}
            </AppButton>
          </div>
        ) : grantsQuery.data?.length ? (
          <ul className="divide-y divide-[var(--app-border)]">
            {grantsQuery.data.map((grant) => {
              const expired = isMcpDateExpired(grant.expires_at);
              const revoked = Boolean(grant.revoked_at);
              const active = !expired && !revoked;
              const expiry = formatMcpDate(grant.expires_at, i18n.language);
              const lastUsed = formatMcpDate(grant.last_used_at, i18n.language);
              return (
                <li key={grant.id} className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-[var(--app-text)]">{grant.client_name}</p>
                    <p className="mt-1 text-xs leading-5 text-[var(--app-text-secondary)]">
                      {t("profile.mcpConsent.status")}: {t(active ? "profile.mcpConsent.active" : revoked ? "profile.mcpConsent.revoked" : "profile.mcpConsent.expired")}
                      {expiry ? ` · ${t("profile.mcpConsent.expires", { date: expiry })}` : ""}
                    </p>
                    <p className="text-xs leading-5 text-[var(--app-text-secondary)]">
                      {t("profile.mcpConsent.lastAuthentication", { date: lastUsed ?? t("profile.mcpConsent.never") })}
                    </p>
                    <p className="text-xs leading-5 text-[var(--app-text-secondary)]">
                      {t("profile.mcpConsent.scopes", { scopes: grant.scopes.join(", ") })}
                    </p>
                  </div>
                  {active ? (
                    <AppButton
                      type="button"
                      tone="dangerSoft"
                      size="sm"
                      isLoading={isRevokingId === grant.id}
                      disabled={isRevokingId !== null}
                      leadingIcon={<FiTrash2 />}
                      onClick={() => void handleRevoke(grant)}
                    >
                      {t("profile.mcpConsent.revoke")}
                    </AppButton>
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="text-sm text-[var(--app-text-secondary)]">{t("profile.mcpConsent.empty")}</p>
        )}
      </div>
    </details>
  );
};
